<script setup lang="ts">
import { formatNumbersWithComma } from "@/utils/numeralHelper";
import Filters from "@/filters";
import { onMounted, ref } from "vue";
import { IStatsKPI, useDashboardStore } from "@/stores/dashboard";
import DashboardService from "@/services/dashboardService";
import { DashboardKPIEnum } from "@/common/enums/dashboardKPIEnum";

const props = defineProps<{
  title: string;
  color: string;
  stats?: number | null;
  icon: string;
  cardKey: DashboardKPIEnum;
  action?: boolean;
  to?: string;
  isRevenue?: boolean;
  isCommission?: boolean;
}>();
const dashboardStore = useDashboardStore();
const loading = ref(false);
const mouseEntered = ref(false);
const fetchKPIData = async () => {
  loading.value = true;
  try {
    const data = await DashboardService.getDashboardStats(props.cardKey);
    dashboardStore.setStatsByKey(props.cardKey, data?.value);
  } finally {
    loading.value = false;
  }
};
watchEffect(async () => {
  dashboardStore.getStatsByKey(props.cardKey)?.stats === null &&
    props.cardKey &&
    (await fetchKPIData());
});
</script>

<template>
  <VCard
    class="stats-card"
    elevation="0"
    variant="flat"
    @mouseenter="mouseEntered = true"
    @mouseleave="mouseEntered = false"
    :loading="loading"
    :class="[
      {
        'is-hovered': mouseEntered,
        'is-actionable': action,
      },
    ]"
    @click="action && to ? $router.push(to) : undefined"
    >
    <VCardText class="stats-card__body">
      <div class="stats-card__icon" v-if="icon">
        <VAvatar size="48" variant="tonal" color="white">
          <VIcon :icon="icon" size="24" color="primary" />
      </VAvatar>
      </div>
      <div class="stats-card__content">
        <p class="stats-card__value">
        <span v-if="isRevenue">
          $
          {{
              formatNumbersWithComma(
                Filters.currency.centsToDollars(Number(stats || 0), 0),
              )
          }}
        </span>
        <span v-else-if="isCommission">
          $
          {{
              Filters.currency.centsToDollarsWithoutToFixed(
                Number(stats || 0),
              )
          }}
        </span>
        <span v-else>{{ stats || 0 }}</span>
        </p>
        <p class="stats-card__label">
          {{ title }}
        </p>
      </div>
    </VCardText>
  </VCard>
</template>

<style scoped lang="scss">
.stats-card {
  border-radius: 18px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  transition: box-shadow 0.2s ease, transform 0.2s ease;
  box-shadow: 0 6px 24px rgba(15, 23, 42, 0.04);
  cursor: default;

  &.is-actionable {
    cursor: pointer;
  }

  &.is-hovered {
    background-color: rgb(var(--v-theme-primary)) !important;

    .stats-card__value,
    .stats-card__label {
      color: rgb(var(--v-theme-on-primary)) !important;
    }

    .stats-card__icon :deep(.v-avatar) {
      background-color: rgba(255, 255, 255, 0.2) !important;

      .v-icon {
        color: rgb(var(--v-theme-on-primary)) !important;
      }
    }
  }
}

.stats-card__body {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem !important;
}

.stats-card__icon :deep(.v-avatar) {
  background-color: #f4f5f9 !important;
  border-radius: 16px !important;
}

.stats-card__value {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #0f172a;
}

.stats-card__label {
  margin: 0;
  margin-top: 0.2rem;
  font-size: 0.95rem;
  color: rgba(15, 23, 42, 0.7);
}
</style>